import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FaGoogle } from 'react-icons/fa';
import { ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Signup = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/signup`,
        { name, email, password },
        { withCredentials: true }
      );
      navigate('/dashboard', { state: { user: response.data } });
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F5F1EA] flex flex-col" data-testid="signup-page">
      <div className="p-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#525252] hover:text-[#0A0A0A] transition-colors text-sm font-medium"
          data-testid="home-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#0A0A0A] flex items-center justify-center mb-6">
              <span className="text-[#B8935A] font-heading font-bold text-2xl">S</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A0A0A] tracking-tight mb-2">Create your account</h1>
            <p className="text-[#525252] text-sm">Two minutes to your first digital card.</p>
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-[#0A0A0A] text-sm font-medium mb-2 block">Full name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                className="bg-white border-[#0A0A0A]/15 focus:border-[#0A0A0A] focus:ring-0 rounded-xl h-12 px-4 text-[#0A0A0A] placeholder:text-[#a3a3a3]"
                data-testid="signup-name-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-[#0A0A0A] text-sm font-medium mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="bg-white border-[#0A0A0A]/15 focus:border-[#0A0A0A] focus:ring-0 rounded-xl h-12 px-4 text-[#0A0A0A] placeholder:text-[#a3a3a3]"
                data-testid="signup-email-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#0A0A0A] text-sm font-medium mb-2 block">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-white border-[#0A0A0A]/15 focus:border-[#0A0A0A] focus:ring-0 rounded-xl h-12 px-4 text-[#0A0A0A] placeholder:text-[#a3a3a3]"
                data-testid="signup-password-input"
                required
                minLength={6}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm" data-testid="signup-error-message">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full font-semibold h-12"
              data-testid="signup-submit-button"
            >
              {loading ? 'Creating account…' : 'Create account'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#0A0A0A]/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-[#F5F1EA] text-[#525252]">or</span>
            </div>
          </div>

          <Button
            onClick={handleGoogleSignup}
            variant="outline"
            className="w-full bg-white border-[#0A0A0A]/15 text-[#0A0A0A] hover:bg-[#0A0A0A]/5 rounded-full font-medium h-12"
            data-testid="google-signup-button"
          >
            <FaGoogle className="mr-2" /> Continue with Google
          </Button>

          <p className="text-center text-[#525252] text-sm mt-8">
            Already have an account?{' '}
            <Link to="/login" className="text-[#0A0A0A] font-semibold hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
